import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputComponent } from "@components/input/input.component";
import { PageComponent } from "@components/page/page.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SearchComponent } from "@components/search/search.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { chain, toLower, upperFirst } from "lodash";
import { take } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { RaskRoller } from "~app/common/models/constants";
import { IContent } from "~app/common/models/validationModel";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { EmailUtils } from "~app/common/utils/email-utils";
import { VirkesleverantorListComponent } from "./components/virkesleverantor-list/virkesleverantor-list.component";
import { LeverantorDialogService } from "./dialog/leverantor-dialog.service";
import { MoveLeverantorerDialogComponent } from "./dialog/move-leverantorer-dialog/move-leverantorer-dialog.component";
import { LeverantorsKlassning } from "./models/leverantorsKlassning";
import { LeverantorsstatusSearchFilter } from "./models/leverantorsstatusSearchFilter";
import { ISearchVirkesleverantor, ISearchVirkesleverantorRequest } from "./models/virkesleverantorModel";
import { VirkesleverantorService } from "./services/virkesleverantor.service";

@Component({
    selector: "rask-virkesleverantor",
    templateUrl: "./virkesleverantor.component.html",
    styleUrls: ["./virkesleverantor.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        ActionButtonComponent,
        HeaderComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        SearchComponent,
        TreeSelectorComponent,
        SelectorComponent,
        SubmitButtonComponent,
        ButtonComponent,
        RadiobuttonGroupComponent,
        VirkesleverantorListComponent,
        TranslatePipe
    ]
})
export class VirkesleverantorComponent implements OnInit {
    protected autoOpenLeverantorId?: number;
    protected form: FormGroup<SearchLeverantorForm>;
    protected leverantorer: ISearchVirkesleverantor[];
    protected leverantorklassningOptions: SelectionItem[] = [
        { text:"A - Ambassadör",
            value: LeverantorsKlassning.A },
        { text:"B - Intressanta",
            value: LeverantorsKlassning.B },
        { text:"C - Övriga",
            value: LeverantorsKlassning.C },
        { text:"D - Vill ej bli kontaktade",
            value: LeverantorsKlassning.D },
        { text:"Ej klassad",
            value: null }
    ];
    protected leverantorsstatusOptions: SelectionItem[] = [
        { value: LeverantorsstatusSearchFilter.Anbudsleverantor,
            text: "Anbudsleverantör" },
        { value: LeverantorsstatusSearchFilter.Virkesleverantor,
            text: "Leverantör" }
    ];
    protected villHaAlmanackanButtons: IRadiobuttonItem[] = [
        { key: true,
            value: "Ja" },
        { key: false,
            value: "Nej" },
        { key: null,
            value: "Alla" }
    ];

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly leverantorDialogService = inject(LeverantorDialogService);
    private readonly leverantorService = inject(VirkesleverantorService);
    private readonly modalService = inject(NgbModal);
    private readonly raskModalService = inject(RaskModalService);

    private static readonly mailtoLinkMaxLength = 2000;

    ngOnInit() {
        this.form = new FormGroup<SearchLeverantorForm>({
            adressParam: new FormControl(""),
            ansvarigVirkeskopare: new FormControl(null),
            limeId: new FormControl(""),
            sok: new FormControl(""),
            telefonParam: new FormControl(""),
            klassning: new FormControl<string[]>(null),
            leverantorsstatus: new FormControl(null),
            villHaAlmanackan: new FormControl<boolean>(null)
        });

        this.activatedRoute.data.pipe(
            take(1)
        ).subscribe(data => {
            if (data.leverantor) {
                this.leverantorer = data.leverantor;
                this.autoOpenLeverantorId = this.leverantorer[0]?.id;
            }
        });
    }

    protected addNewAnbudsleverantor = () => {
        this.leverantorDialogService.open();
    };

    protected canMoveLeverantorer = () => this.authenticationService.hasRoles(`${RaskRoller.role.Kopleda}`);

    protected formatter = (item: SelectionItem) => item?.value;

    protected moveLeverantorer = () => {
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "xl"
        } as NgbModalOptions;
        const modalRef = this.modalService.open(MoveLeverantorerDialogComponent, options);

        modalRef.result.then(() => {
            // Do nothing
        });
    };

    protected onMailAllClicked = () => {
        if(this.leverantorer) {
            const mailAdresses = chain(this.leverantorer)
                .filter(leverantor => EmailUtils.isValid(leverantor.epost))
                .map(leverantor => leverantor.epost)
                .uniq()
                .value();

            const link = `mailto:?bcc=${mailAdresses.join(";")}`;

            if(link.length > VirkesleverantorComponent.mailtoLinkMaxLength) {
                this.raskModalService.error("Fel",
                ({
                    message: `Adressernas sammanlagda längd (${link.length}) ` +
                    `överskrider maxlängden (${VirkesleverantorComponent.mailtoLinkMaxLength}). ` +
                    `Specificera sökningen för att undvika problemet.`
                } as unknown) as IContent)
                    .subscribe();
            } else{
                window.location.href = link;
            }
        }
    };

    protected reset() {
        this.form.reset();
        // tree-selector komponenten klarar inte av att hantera reset och därför behövs en manuell återställnnning
        this.form.patchValue({
            klassning: []
        });
        this.leverantorer = null;
    }

    protected search() {
        if (!this.form.value.leverantorsstatus) {
            this.form.value.leverantorsstatus = "";
        }

        const request: ISearchVirkesleverantorRequest = {
            ...this.form.value
        };

        const query = this.leverantorService.searchForVirkeskopsleverantor(request);
        this.busyService.addWork(query).subscribe((res) => {
            res.forEach((lev) => {
                lev.klassning = upperFirst(lev.klassning);
                lev.adress = upperFirst(lev.adress);
                lev.epost = toLower(lev.epost);
            });

            this.leverantorer = res;
        });
    }

    protected getAnvandareTypeAhead = (input: string) => this.leverantorService.getAnvandareTypeAhead(input);

    protected textFormatter = (item: SelectionItem) => item?.text;
}

type SearchLeverantorForm = {
    adressParam: FormControl<string>;
    ansvarigVirkeskopare: FormControl<string>;
    klassning:  FormControl<string[]>;
    leverantorsstatus:  FormControl<string>;
    limeId: FormControl<string>;
    sok:  FormControl<string>;
    telefonParam: FormControl<string>;
    villHaAlmanackan: FormControl<boolean>;
};
